import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SkeletonTheme } from 'react-loading-skeleton';
import './Assets/Css/Variables.css';
import 'react-loading-skeleton/dist/skeleton.css';
import './Assets/Css/Common.css';
import styles from './Assets/Css/Style.module.css';
import { Navigation } from './Navigation/Navigation';
import { SideLayout } from './Components/SideLayout/SideLayout';
import { getBackPath } from './Utils/NavigationMap';
import BackHandler from './Utils/BackHandler';
import ScrollToTop from './Utils/Hooks/ScrollTop';
import { ExitCard } from './Components/Popup/ExitCard/ExitCard';
import { OfflineBanner } from './Components/OfflineBanner/OfflineBanner';
import ErrorBoundary from './Components/ErrorBoundary';
import { useAppointment } from './Context/Appointment/AppointmentContext';
import { useReleaseBookingHold } from './Utils/Hooks/useReleaseBookingHold';
import { useBookingSession } from './Context/BookingSession/BookingSessionContext';
import { getSessionStorageValue, STORAGE_KEYS } from './Utils/SessionStorage';
import { useCrossTabSync } from './Utils/Hooks/useCrossTabSync';
import { setSessionStorageValue } from './Utils/SessionStorage';
import useNetworkStatus from './Utils/Hooks/useNetworkStatus';
import { usePreventBrowserClose } from './Utils/Hooks/usePreventBrowserClose';
import { DiscardPopup } from './Components/Popup/DiscardPopup/DiscardPopup';
import { useKeyboardInset } from './Utils/Hooks/useKeyboardInset';
import { ApplicationGate } from './Components/ApplicationGate/ApplicationGate';
import { useCookiebotFooterOffset } from './Utils/Hooks/useCookiebotFooterOffset';
import { useCookiebotLongPress } from './Utils/Hooks/useCookiebotLongPress';
import { useCookiebotLauncher } from './Utils/Hooks/useCookiebotLauncher';
import { useCookiebotMobileBackdrop } from './Utils/Hooks/useCookiebotMobileBackdrop';

const BackHandlerWithCart = () => {
  const { appointmentTypeID } = useAppointment();
  const { bookingID } = useBookingSession();
  const { releaseHold } = useReleaseBookingHold();

  const handleCustomBack = useCallback(async (currentPath, navigate) => {
    // Browser back from Review → Time: release hold and stop the countdown.
    const holdId = bookingID || getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
    if (currentPath === '/review-and-confirm-booking' && holdId) {
      await releaseHold();
    }
    const target = getBackPath(currentPath, appointmentTypeID);
    navigate(target, { replace: true });
  }, [appointmentTypeID, bookingID, releaseHold]);

  return <BackHandler handleCustomBack={handleCustomBack} />;
};

function Application() {
  useKeyboardInset();
  const { pathname } = useLocation();
  useCrossTabSync();
  usePreventBrowserClose(); // Warn when closing tab with active booking

  // Initialize membership on app startup
  useEffect(() => {
    if (getSessionStorageValue(STORAGE_KEYS.MEMBERSHIP) === null) {
      setSessionStorageValue(STORAGE_KEYS.MEMBERSHIP, true);
    }
  }, []);

  // Keep the sticky header's colour in sync with the page background so it
  // blends into the gradient while scrolling at every responsive breakpoint.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const root = document.documentElement;

    const parseColor = (value) => {
      const v = (value || "").trim();
      let hex = v.startsWith("#") ? v.slice(1) : v;
      if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
      if (hex.length !== 6) return null;
      const n = parseInt(hex, 16);
      if (Number.isNaN(n)) return null;
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    };

    const cs = getComputedStyle(document.body);
    const primary = parseColor(cs.getPropertyValue("--BodyPrimaryColor")) || [236, 209, 192];
    const secondary = parseColor(cs.getPropertyValue("--BodySecondaryColor")) || [255, 255, 255];

    let ticking = false;
    let rafId = null;
    let headerObserver = null;
    let bodyObserver = null;
    let mutationObserver = null;
    let observedHeader = null;

    const update = () => {
      ticking = false;
      rafId = null;
      const header = document.querySelector(".sticky-header");
      const headerHeight = header ? header.offsetHeight : 0;
      const pageHeight = document.body.scrollHeight || 1;
      const computedStyles = getComputedStyle(document.body);
      const configuredStop = Number.parseFloat(
        computedStyles.getPropertyValue("--BodyGradientStop")
      );
      const gradientStopPercent = Number.isFinite(configuredStop)
        ? configuredStop
        : 75;
      const stop = (gradientStopPercent / 100) * pageHeight;
      const sampleY = window.scrollY + headerHeight;
      const t = stop > 0 ? Math.min(1, Math.max(0, sampleY / stop)) : 0;
      const mix = primary.map((p, i) => Math.round(p + (secondary[i] - p) * t));
      root.style.setProperty("--sticky-header-bg", `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`);
    };

    const scheduleUpdate = () => {
      if (ticking) return;
      ticking = true;
      rafId = window.requestAnimationFrame(update);
    };

    const observeHeader = () => {
      const nextHeader = document.querySelector(".sticky-header");

      if (nextHeader === observedHeader) {
        scheduleUpdate();
        return;
      }

      headerObserver?.disconnect();
      headerObserver = null;
      observedHeader = nextHeader;

      if (observedHeader && typeof ResizeObserver !== "undefined") {
        headerObserver = new ResizeObserver(scheduleUpdate);
        headerObserver.observe(observedHeader);
      }

      scheduleUpdate();
    };

    observeHeader();
    const routeRafId = window.requestAnimationFrame(observeHeader);

    if (typeof ResizeObserver !== "undefined") {
      bodyObserver = new ResizeObserver(scheduleUpdate);
      bodyObserver.observe(document.body);
    }

    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(() => {
        const currentHeader = document.querySelector(".sticky-header");
        if (currentHeader !== observedHeader) observeHeader();
      });
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.cancelAnimationFrame(routeRafId);
      if (rafId != null) window.cancelAnimationFrame(rafId);
      headerObserver?.disconnect();
      bodyObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [pathname]);


  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    if (!isOnline) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [isOnline]);


  return (
    <ErrorBoundary componentName="App">
      <BackHandlerWithCart />
      <ScrollToTop />
      <SkeletonTheme className="skeleton-theme">
        <div
          className={`${styles.wrapper} wrapper ${
            pathname === "/" ? styles.wrapperHome : ""
          }`}
        >
          {isOnline ? null : <OfflineBanner/>}
          <ErrorBoundary componentName="Sidebar">
            <SideLayout />
          </ErrorBoundary>

          <ErrorBoundary componentName="Navigation">
            <Navigation />
          </ErrorBoundary>

          <ErrorBoundary componentName="Exit Card">
            <ExitCard />
          </ErrorBoundary>

          <ErrorBoundary componentName="Discard Popup">
            <DiscardPopup />
          </ErrorBoundary>

        </div>
      </SkeletonTheme>
    </ErrorBoundary>
  );
}

export default function App() {
  useCookiebotFooterOffset();
  useCookiebotLongPress();
  useCookiebotLauncher();
  useCookiebotMobileBackdrop();
  return <ApplicationGate><Application /></ApplicationGate>;
}


















// import React from 'react';
// import './Assets/Css/Variables.css';
// import 'react-loading-skeleton/dist/skeleton.css'
// import { SkeletonTheme } from 'react-loading-skeleton';
// import './Assets/Css/Common.css';
// import styles from './Assets/Css/Style.module.css'
// import { Header } from './Components/Header/Header';
// import { Navigation } from './Navigation/Navigation';
// import { Cart } from './Components/Cart/Cart';
// import { CartProvider } from './Context/Cart/CartContext';
// import { ExitCard } from './Components/ExitCard/ExitCard';
// import { SideLayout } from './Components/SideLayout/SideLayout';
// import ScrollToTop from './Utils/Hooks/ScrollTop';
// import useDisableForwardButton from './Utils/Hooks/DisableFwrdButton';
// // import Loader from './Components/Loader/Loader';

// function App() {
//   // useDisableForwardButton();
//   return (
//     <CartProvider>
//       <ScrollToTop />
//       <SkeletonTheme className="skeleton-theme">
//         <div className={`${styles.wrapper} wrapper`}>
//           {/* <Loader/> */}
//           <Header/>
//           <SideLayout />
//           <Navigation/>
//           <Cart/>
//           <ExitCard/>
//         </div>
//       </SkeletonTheme>
//     </CartProvider>
//   );
// }

// export default App;
